import { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:
    "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const VEHICLE_EMOJI = {
  moto:      "🛵",
  bicicleta: "🚲",
  carro:     "🚗",
  camion:    "🚛",
  otro:      "📦",
};

function buildVehicleIcon(vehicleType) {
  const emoji = VEHICLE_EMOJI[vehicleType] ?? "🛵";
  return L.divIcon({
    html: `<div style="
      font-size: 28px;
      line-height: 1;
      filter: drop-shadow(0 2px 4px rgba(0,0,0,0.35));
      animation: bounce 1s infinite alternate;
    ">${emoji}</div>`,
    className: "",
    iconSize: [36, 36],
    iconAnchor: [18, 36],
    popupAnchor: [0, -36],
  });
}

function buildDestIcon() {
  return L.divIcon({
    html: `<div style="font-size:26px;line-height:1;filter:drop-shadow(0 2px 4px rgba(0,0,0,0.3))">🏠</div>`,
    className: "",
    iconSize: [28, 28],
    iconAnchor: [14, 28],
  });
}

function MapPanner({ position, routeCoords }) {
  const map = useMap();
  const fitted = useRef(false);

  // Fit bounds to route once when coords arrive
  useEffect(() => {
    if (fitted.current || routeCoords.length < 2) return;
    map.fitBounds(L.latLngBounds(routeCoords), { padding: [20, 20], animate: false });
    fitted.current = true;
  }, [routeCoords, map]);

  // Follow ryder if no route yet
  useEffect(() => {
    if (fitted.current) return;
    map.flyTo([position.lat, position.lng], map.getZoom(), { animate: true, duration: 1 });
    // eslint-disable-next-line
  }, [position.lat, position.lng]);

  return null;
}

export default function RyderLiveMap({ position, vehicleType, deliveryLocation }) {
  const [routeCoords, setRouteCoords] = useState([]);
  const lastFetch = useRef(null);

  // Fetch OSRM route ryder → client, throttled to every 30s or >50m moved
  useEffect(() => {
    if (!position?.lat || !position?.lng) return;
    if (!deliveryLocation?.lat || !deliveryLocation?.lng) return;

    const now = Date.now();
    const prev = lastFetch.current;
    if (prev) {
      const dist = Math.abs(prev.lat - position.lat) + Math.abs(prev.lng - position.lng);
      if (dist < 0.0005 && now - prev.ts < 30000) return; // <~50m and <30s
    }
    lastFetch.current = { lat: position.lat, lng: position.lng, ts: now };

    fetch(
      `https://router.project-osrm.org/route/v1/driving/` +
      `${position.lng},${position.lat};${deliveryLocation.lng},${deliveryLocation.lat}` +
      `?overview=full&geometries=geojson`
    )
      .then((r) => r.json())
      .then((data) => {
        const coords = data.routes?.[0]?.geometry?.coordinates;
        if (coords) setRouteCoords(coords.map(([lng, lat]) => [lat, lng]));
      })
      .catch(() => {});
  }, [position, deliveryLocation]);

  if (!position?.lat || !position?.lng) return null;

  const vehicleIcon = buildVehicleIcon(vehicleType);
  const destIcon    = buildDestIcon();
  const label = `${VEHICLE_EMOJI[vehicleType] ?? "🛵"} Domiciliario en camino`;

  return (
    <div
      className="rounded-xl overflow-hidden mt-3 border border-blue-100"
      style={{ height: 220 }}
    >
      <MapContainer
        center={[position.lat, position.lng]}
        zoom={15}
        style={{ height: "100%", width: "100%" }}
        zoomControl={false}
        scrollWheelZoom={false}
        attributionControl={false}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

        {/* Ryder marker */}
        <Marker position={[position.lat, position.lng]} icon={vehicleIcon}>
          <Popup>{label}</Popup>
        </Marker>

        {/* Destination marker */}
        {deliveryLocation?.lat && deliveryLocation?.lng && (
          <Marker position={[deliveryLocation.lat, deliveryLocation.lng]} icon={destIcon} />
        )}

        {/* Route polyline */}
        {routeCoords.length > 1 && (
          <Polyline positions={routeCoords} color="#7c3aed" weight={4} opacity={0.75} />
        )}

        <MapPanner position={position} routeCoords={routeCoords} />
      </MapContainer>
    </div>
  );
}
